export const CUSTOM_SCHEMA_TYPE_FORMAT = {
  media: 'media',
  folder: 'folder',
  richText: 'rich-text',
  slug: 'slug',
  ref: 'ref',
};

export const SCHEMA_TYPE_PREFIX = {
  ref: 'ref:',
  media: 'media:',
  slug: 'slug:',
};

/** @typedef {{[key:string]: ContentTranslationStatus}} ContentTranslationStatusEnum */
/** @type {ContentTranslationStatusEnum} */
export const TRANSLATIONS_STATUSES = {
  draft: 'draft',
  published: 'published',
};
/**
 * @callback CleanDataFromAPI Remove API specific prefixes or suffixes from data
 * @param {ContentTranslation} contentTranslation
 * @returns {ContentTranslation}
 */
/** @type {CleanDataFromAPI} */
export const cleanDataFromAPI = (contentTranslation) => {
  /**
   * @typedef {object} ContentTranslation
   * @property {object} data
   * @property {Omit<ContentTranslation, 'data'>} rest
   */
  const { data, ...rest } = contentTranslation;
  // To avoid having the recursively check all the object, we stringify it
  const stringifiedData = JSON.stringify(data);
  // Clean all occurences at once
  const cleanedData = stringifiedData.replace(/slug:|media:|ref:/g, '');

  return { data: JSON.parse(cleanedData), ...rest };
};
/**
 * @callback getContentTranslationStatusUIConfig Get UI Définition of a content by its status
 * @param {ContentTranslationStatus} status
 * @returns {{color: string, label: string, backgroundColor: string, actionLabel: string}}
 */
/** @type {getContentTranslationStatusUIConfig} */
export const getContentTranslationStatusUIConfig = (status) => {
  const config = {
    [TRANSLATIONS_STATUSES.draft]: {
      color: 'primary100',
      label: `cortizol.contentTranslation.${TRANSLATIONS_STATUSES.draft}`,
      backgroundColor: 'primary',
      actionLabel: 'cortizol.contentTranslation.saveAction',
    },
    [TRANSLATIONS_STATUSES.published]: {
      color: 'success100',
      label: `cortizol.contentTranslation.${TRANSLATIONS_STATUSES.published}`,
      backgroundColor: 'success',
      actionLabel: 'cortizol.contentTranslation.publishAction',
    },
  };

  return config[status];
};

/**
 * @callback CmsApiSanitation Sanitize a form value to match the API schema
 * @param {Property} property
 * @param {FormValues} formValue
 * @returns {{[key:string]: string|string[]|Property[]}}
 */
/** @type {CmsApiSanitation} */
export const CmsApiSanitation = (property, formValue) => {
  let newValue = null;

  switch (property?.type) {
    case 'string': {
      if (property.format?.includes(CUSTOM_SCHEMA_TYPE_FORMAT.media)) {
        newValue = `${SCHEMA_TYPE_PREFIX.media}${formValue}`;
      } else if (property.format?.includes(CUSTOM_SCHEMA_TYPE_FORMAT.slug)) {
        newValue = `${SCHEMA_TYPE_PREFIX.slug}${formValue}`;
      } else if (property.format?.includes(CUSTOM_SCHEMA_TYPE_FORMAT.ref)) {
        newValue = `${SCHEMA_TYPE_PREFIX.ref}${formValue}`;
      } else {
        newValue = formValue;
      }

      break;
    }
    case 'array': {
      newValue = formValue;

      if (property?.items?.format?.includes(CUSTOM_SCHEMA_TYPE_FORMAT.media)) {
        newValue = formValue?.map((
          /** @type {string} */ value,
        ) => `${SCHEMA_TYPE_PREFIX.media}${value}`);
      }

      if (property?.items?.format?.includes(CUSTOM_SCHEMA_TYPE_FORMAT.ref)) {
        newValue = formValue?.map((
          /** @type {string} */ value,
        ) => `${SCHEMA_TYPE_PREFIX.ref}${value}`);
      }

      if (property?.items?.type === 'object' && (property?.items?.oneOf)) {
        newValue = formValue?.map((objectToSanitize) => ({
          ...objectToSanitize,
          data: Object.keys(objectToSanitize.data)?.reduce((acc, dataKey) => {
            const schemaOfValueType = property?.items?.oneOf
              ?.find(
                (oneOfItem) => {
                  const pattern = oneOfItem?.properties?.type?.pattern.replace(/[\^$]/g, '');
                  return pattern === objectToSanitize?.type;
                },
              );
            const sanitizedValue = CmsApiSanitation(
              schemaOfValueType?.properties?.data?.properties[dataKey],
              objectToSanitize?.data[dataKey],
            );
            return { ...acc, [dataKey]: sanitizedValue };
          }, {}),
        }));
      }

      break;
    }

    default: {
      newValue = formValue;
    }
  }

  return newValue;
};
